import { UseSwapDataReturn } from '@/application/read/swap/useSwapData';
import Button from '@/presentation/components/elements/Button';
import Icon, { Icons } from '@/presentation/components/elements/Icon';
import { useModalContext } from '@/presentation/context/modal';
import ellipsis from '@/presentation/utils/ellipsis';
import getVaultIcon from '@/presentation/utils/getVaultIcon';
import { useNetwork } from '@nftx/react';
import { Transaction } from 'nftx.js';
import { useTranslations } from 'use-intl';
import { Address, Chain, TransactionReceipt } from 'viem';
import { useWalletClient } from 'wagmi';

const SwapSuccess = ({
  data,
  transaction,
  receipt,
  chain,
  onClose,
}: {
  data: UseSwapDataReturn;
  transaction?: Transaction;
  receipt?: TransactionReceipt<any>;
  chain: Chain;
  onClose: () => void;
}) => {
  const network = useNetwork();
  const t = useTranslations('Swap.Success');
  const txHash = transaction?.hash;

  const { data: wallet } = useWalletClient();
  const { showCreateInventoryPositionModal, showCreateLiquidityPositionModal } =
    useModalContext();

  return (
    <div className="p-6 bg-gray-50 max-w-lg mx-auto">
      <h2 className="text-lg text-center mb-2 font-bold">{t('title')}</h2>
      <div className="p-4 flex justify-center items-center">
        <Icon name={Icons.CHECK} size={48} className="text-green-500" />
      </div>
      <p className="text-center mb-8">
        <a
          href={`${chain.blockExplorers?.default.url}/tx/${txHash}`}
          className="inline-flex items-center hover:underline"
          target="_blank"
          rel="nofollow noreferrer"
        >
          {t('tx', { hash: ellipsis(txHash) })}
          <Icon name={Icons.EXTERNAL} size={12} className="ml-1" />
        </a>
      </p>
      {data?.quote?.type === 'erc20' && data?.buyVault && (
        <section className="rounded-md bg-gray-100 border border-gray-300 divide-y divide-gray-300">
          <h3 className="p-4 text-center font-medium">
            {'Not sure what to do next?'}
          </h3>
          <ul className="divide-y divide-gray-300">
            <li className="p-4 flex justify-between">
              <div>
                <h4>{`Add ${data.buySymbol} token to your wallet`}</h4>
              </div>
              <button
                onClick={() =>
                  wallet?.watchAsset({
                    options: {
                      address: data?.buyVault?.id as Address,
                      decimals: 18,
                      symbol: data.buySymbol,
                      image: getVaultIcon(
                        data?.buyVault?.vaultId as string,
                        network
                      ),
                    },
                    type: 'ERC20',
                  })
                }
                className="underline hover:no-underline font-bold"
              >
                {'Add'}
              </button>
            </li>
            <li className="p-4 flex justify-between">
              <div>
                <h4>{`Deposit ${data.buyVault.asset.name} in ${data.buyVault?.token.name} vault`}</h4>
                <span className="bg-gray-500 text-gray-50 text-xs rounded-md py-0.5 px-1 inline-block mr-2">
                  {'Earns vault fees'}
                </span>
              </div>
              <button
                onClick={() =>
                  showCreateInventoryPositionModal({
                    vaultId: data.buyVault?.vaultId as string,
                    using: data?.quote?.type === 'erc20' ? 'vtoken' : 'nft',
                  })
                }
                className="underline hover:no-underline font-bold"
              >
                {'Stake'}
              </button>
            </li>
            <li className="p-4 flex justify-between">
              <div>
                <h4>{`Provide Liquidity for ${data.buyVault.token.name} vault`}</h4>
                {
                  // @TODO add APR
                }
                <span className="bg-gray-500 text-gray-50 text-xs rounded-md py-0.5 px-1 inline-block mr-2">
                  {'Earns vault fees'}
                </span>
                <span className="bg-gray-800 text-gray-100 text-xs rounded-md py-0.5 px-1 inline-block">
                  {'Earns trading fees'}
                </span>
              </div>
              <button
                onClick={() =>
                  showCreateLiquidityPositionModal({
                    vaultId: data.buyVault?.vaultId as string,
                    using: data?.quote?.type === 'erc20' ? 'vtoken' : 'nft',
                  })
                }
                className="underline hover:no-underline font-bold"
              >
                {'LP'}
              </button>
            </li>
          </ul>
        </section>
      )}
      <div className="mt-4 flex justify-center">
        <Button type="button" variant="primary" onClick={onClose}>
          {'Close'}
        </Button>
      </div>
    </div>
  );
};

export default SwapSuccess;
